/**
 * File: AddLeadDialog.tsx
 * Path: apps/admin/src/shared/components/AddLeadDialog.tsx
 * Description: Adds a lead by hand (a referral, a call, someone met in
 * person), then opens its page so notes can go straight on it.
 */

import React from "react";
import { LEAD_SOURCE_CHOICES, LEAD_SOURCE_LABELS, type LeadSource } from "@idk/config";
import { Button } from "@idk/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { useAddLeadDialogController } from "../hooks/useAddLeadDialogController";
import { DuplicateWarning } from "./DuplicateWarning";
import { fieldClass, labelClass } from "./LeadPrimitive";
const Field: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  error?: string;
  className?: string;
}> = ({ id, label, value, onChange, type = "text", inputMode, autoComplete, error, className }) => (
  <div className={className ?? "space-y-stack-xs"}>
    <label htmlFor={id} className={labelClass}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      className={fieldClass}
    />
    {error && (
      <p id={`${id}-error`} className="text-xs text-destructive">
        {error}
      </p>
    )}
  </div>
);

export const AddLeadDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const { draft, errors, set, text, partners, pending, handleOpenChange, handleSubmit } = useAddLeadDialogController(open, onOpenChange);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-stack-md overflow-y-auto rounded-none p-gutter-md">
        <DialogHeader className="space-y-stack-xs text-left">
          <DialogTitle className="pr-gutter-lg text-base">Add a lead</DialogTitle>
          <DialogDescription className="text-xs">
            Only the business name is needed. It starts in New.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-stack-md">
          <Field
            id="add-lead-name"
            label="Business name (required)"
            value={draft.name}
            onChange={text("name")}
            autoComplete="organization"
            error={errors.name}
            className="space-y-stack-xs sm:col-span-2"
          />
          <Field id="add-lead-contact" label="Contact person" value={draft.contactName} onChange={text("contactName")} autoComplete="name" />
          <Field id="add-lead-phone" label="Phone" type="tel" value={draft.phone} onChange={text("phone")} autoComplete="tel" />
          <Field
            id="add-lead-email"
            label="Email"
            type="email"
            value={draft.email}
            onChange={text("email")}
            autoComplete="email"
            error={errors.email}
          />
          <Field id="add-lead-website" label="Website" type="url" value={draft.website} onChange={text("website")} autoComplete="url" />
          <Field id="add-lead-area" label="Area" value={draft.area} onChange={text("area")} />
          <Field id="add-lead-niche" label="Niche (what they do)" value={draft.niche} onChange={text("niche")} />
          <Field
            id="add-lead-value"
            label="Estimated value in rand (optional)"
            inputMode="decimal"
            value={draft.value}
            onChange={text("value")}
            error={errors.value}
          />
          <Field
            id="add-lead-follow-up"
            label="Next follow-up (optional)"
            type="date"
            value={draft.nextFollowUp}
            onChange={text("nextFollowUp")}
          />
          <div className="space-y-stack-xs">
            <label htmlFor="add-lead-source" className={labelClass}>
              Where did they come from?
            </label>
            <select
              id="add-lead-source"
              className={fieldClass}
              value={draft.source}
              onChange={(e) => set("source", e.target.value as LeadSource)}
            >
              {LEAD_SOURCE_CHOICES.map((source) => (
                <option key={source} value={source}>
                  {LEAD_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          </div>
          {draft.source === "referral" && (
            <div className="space-y-stack-xs">
              <label htmlFor="add-lead-partner" className={labelClass}>
                Who sent them? (optional)
              </label>
              <select
                id="add-lead-partner"
                className={fieldClass}
                value={draft.affiliateId}
                disabled={partners.isLoading}
                onChange={(e) => set("affiliateId", e.target.value)}
              >
                <option value="">{partners.isLoading ? "Loading…" : "Not a partner / not sure"}</option>
                {(partners.data ?? []).map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <DuplicateWarning
            details={{ name: draft.name, phone: draft.phone, email: draft.email, website: draft.website }}
          />
          <label className="flex min-h-11 items-center gap-stack-xs text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={draft.marketingConsent}
              onChange={(e) => set("marketingConsent", e.target.checked)}
              className="h-5 w-5 shrink-0"
            />
            They agreed to hear from us by email/SMS/WhatsApp
          </label>
          <div className="flex flex-wrap gap-stack-sm sm:col-span-2">
            <Button type="submit" className="h-11 rounded-none" disabled={pending}>
              {pending ? "Saving…" : "Save lead"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-11 rounded-none"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
